import { IRegistry, RegistryIdentityType } from './registry.interface';

export type EntryJobActionType = 'CREATE' | 'UPDATE' | 'DELETE';

export type EntryJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export const ENTRY_JOB_ACTION_LABELS: Record<EntryJobActionType, string> = {
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
};

export const ENTRY_JOB_STATUS_LABELS: Record<EntryJobStatus, string> = {
    pending: 'Pending',
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
};

export interface IEntryImage {
    image_id: number;
    url: string | null;
}

export interface IEntry {
    entry_id: string;
    friendly_name: string | null;
    images: IEntryImage[];
    custom_fields: Record<string, unknown> | null;
    created_at: string | null;
    updated_at: string | null;
    plate_text?: string;
}

export type EntryColumn = 'select' | 'friendly_name' | 'plate_text' | 'created_at' | 'images' | 'actions';

export const ENTRY_COLUMNS: Record<RegistryIdentityType, EntryColumn[]> = {
    FACE:          ['select', 'friendly_name', 'created_at', 'images', 'actions'],
    LICENSE_PLATE: ['select', 'plate_text', 'friendly_name', 'created_at', 'images', 'actions'],
};

export interface IEntryJob {
    id: string;
    registry_id: string;
    action_type: EntryJobActionType;
    parameters: Record<string, unknown>;
    status: EntryJobStatus;
    creation_timestamp: string;
    deletion_timestamp: string | null;
    created_by: string;
    dispatched_at: string | null;
    completed_at: string | null;
    dispatch_count: number;
}

export interface ICreateEntryJobRequest {
    registry_id: string;
    identity_type: RegistryIdentityType;
    action_type: EntryJobActionType;
    parameters: Record<string, unknown>;
}

export interface IBulkCreateEntryJobItem {
    action_type: EntryJobActionType;
    parameters: Record<string, unknown>;
}

export interface IBulkCreateEntryJobRequest {
    registry_id: string;
    identity_type: RegistryIdentityType;
    entry_jobs: IBulkCreateEntryJobItem[];
}

/** An item whose images still need uploading before it can be sent to the API. */
export interface IBulkCreateEntryJobDraft extends IBulkCreateEntryJobItem {
    files: File[];
}

export type BulkCreateEntryJobItemStatus = 'CREATED' | 'REJECTED' | 'NOT_CREATED';

export type EntryJobRejectionReason =
    | 'INVALID_REQUEST'
    | 'UNKNOWN_FIELD'
    | 'MISSING_REGISTRY_ID'
    | 'INVALID_REGISTRY_ID'
    | 'MISSING_IDENTITY_TYPE'
    | 'INVALID_IDENTITY_TYPE'
    | 'MISSING_ENTRY_JOBS'
    | 'INVALID_ENTRY_JOBS'
    | 'NO_ENTRY_JOBS'
    | 'TOO_MANY_ENTRY_JOBS'
    | 'INVALID_ENTRY_JOB'
    | 'MISSING_ENTRY_ID'
    | 'INVALID_ENTRY_ID'
    | 'MISSING_PLATE_TEXT'
    | 'INVALID_PLATE_TEXT'
    | 'PLATE_TEXT_EXISTS'
    | 'PLATE_TEXT_DUPLICATED_IN_REQUEST'
    | 'MISSING_FRIENDLY_NAME'
    | 'INVALID_FRIENDLY_NAME'
    | 'MISSING_STAGING_PATHS'
    | 'INVALID_STAGING_PATHS'
    | 'INVALID_REMOVE_IMAGE_IDS'
    | 'MISSING_ACTION_TYPE'
    | 'INVALID_ACTION_TYPE'
    | 'MISSING_PARAMETERS'
    | 'INVALID_PARAMETERS'
    | 'UNKNOWN_PARAMETER';

export const ENTRY_JOB_REJECTION_MESSAGES: Record<EntryJobRejectionReason, string> = {
    INVALID_REQUEST: 'The request is invalid',
    UNKNOWN_FIELD: 'The request contains an unsupported field',
    MISSING_REGISTRY_ID: 'A registry is required',
    INVALID_REGISTRY_ID: 'The registry is invalid',
    MISSING_IDENTITY_TYPE: 'A registry type is required',
    INVALID_IDENTITY_TYPE: 'The registry type is invalid',
    MISSING_ENTRY_JOBS: 'At least one entry is required',
    INVALID_ENTRY_JOBS: 'The entries are invalid',
    NO_ENTRY_JOBS: 'At least one entry is required',
    TOO_MANY_ENTRY_JOBS: 'Too many entries for one batch',
    INVALID_ENTRY_JOB: 'This entry is not valid',
    MISSING_ENTRY_ID: 'Entry is missing an identifier',
    INVALID_ENTRY_ID: 'Entry identifier is invalid',
    MISSING_PLATE_TEXT: 'Plate text is required',
    INVALID_PLATE_TEXT: 'Plate text is invalid',
    PLATE_TEXT_EXISTS: 'Plate text already exists',
    PLATE_TEXT_DUPLICATED_IN_REQUEST: 'Plate text is duplicated in this batch',
    MISSING_FRIENDLY_NAME: 'Name is required',
    INVALID_FRIENDLY_NAME: 'Name is invalid',
    MISSING_STAGING_PATHS: 'At least one image is required',
    INVALID_STAGING_PATHS: 'Entry images are invalid',
    INVALID_REMOVE_IMAGE_IDS: 'Images to remove are invalid',
    MISSING_ACTION_TYPE: 'Entry action is required',
    INVALID_ACTION_TYPE: 'Entry action is invalid',
    MISSING_PARAMETERS: 'Entry details are required',
    INVALID_PARAMETERS: 'Entry details are invalid',
    UNKNOWN_PARAMETER: 'Entry details contain an unsupported field',
};

/**
 * The API's schema validation errors, as the schema itself reports them.
 *
 * A leaf is the list of rejection reasons for one field; a branch is a nested
 * object, keyed by field name, or -- under `entry_jobs` -- by the string index
 * of the entry that failed. `_schema` keys an error about an object rather
 * than about a field in it.
 */
export interface IEntryJobValidationErrors {
    [key: string]: IEntryJobValidationErrors | string[];
}

/**
 * The wording of a schema validation failure, split by what it is about.
 *
 * `request` holds problems with the request itself; `entries` holds problems
 * with one submitted entry, keyed by that entry's position in the request.
 */
export interface IEntryJobValidationMessages {
    request: string[];
    entries: Map<number, string[]>;
}

export interface IBulkCreateEntryJobItemRejection {
    reason: EntryJobRejectionReason;
    field: string;
}

export interface IBulkCreateEntryJobItemResult {
    index: number;
    action_type: EntryJobActionType;
    status: BulkCreateEntryJobItemStatus;
    entry_job: IEntryJob | null;
    rejections: IBulkCreateEntryJobItemRejection[];
}

export interface IBulkCreateEntryJobResult {
    registry_id: string;
    items: IBulkCreateEntryJobItemResult[];
}

/** The body of an entry job request the API refused, which carries a message or a validation tree. */
export interface IEntryJobErrorBody {
    message?: string;
}

/** A refused bulk request also reports the items it rejected. */
export interface IBulkCreateEntryJobErrorBody extends IEntryJobErrorBody {
    items?: IBulkCreateEntryJobItemResult[];
}

export interface IUploadFileInfo {
    filename: string;
    signed_url: string;
    staging_path: string;
}

export interface ISignedUrlResponse {
    upload_id: string;
    files: IUploadFileInfo[];
}

export interface IGetSignedUrlsRequest {
    registry_id: string;
    filenames: string[];
}

export interface IListEntryJobsRequest {
    registry_id: string;
    status?: EntryJobStatus;
}

export interface EntryJobTotals {
    pending: number;
    failed: number;
}

export interface IModifyEntryParameters extends Record<string, unknown> {
    friendly_name?: string | null;
    plate_text?: string | null;
    staging_paths?: string[];
}

export type ICreateEntryParameters = IModifyEntryParameters;

export interface IUpdateEntryParameters extends IModifyEntryParameters {
    entry_id: string;
    friendly_name: string | null;
    remove_image_ids: number[];
}

export interface IModifyEntryDialogData {
    registryId: string;
    identityType: RegistryIdentityType;
    entry?: IEntry;
    plateText?: string;
    initialFiles?: File[];
    registries?: IRegistry[];
    allowImageUpload?: boolean;
}

export interface IBulkCreateEntriesDialogData {
    registryId: string;
    identityType: RegistryIdentityType;
}

export interface IEntryDetailDialogData {
    entry: IEntry;
    identityType: RegistryIdentityType;
}

export interface IListEntriesRequest {
    registry_id: string;
}
